import { Dialog } from "@/components/ui/Dialog"
import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { Button } from "../ui/Button"
import { resetDialogs } from "@/store/dialogs-state"

interface IntakeSurveyQuestion {
	id: string
	question: string
	choices: string[]
}

interface IntakeSurveyDialogProps {
	readonly resultHandler: (result: Record<string, string>) => void
}

export function IntakeSurveyDialog(props: IntakeSurveyDialogProps) {
	const { t } = useTranslation("components")
	const [selected, setSelected] = useState<string | null>(null)
	const [result, setResult] = useState<Record<string, string>>({})
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)

	useEffect(() => {
		if (selected && currentQuestion) {
			setResult({ ...result, [currentQuestion.id]: selected })
		}
	}, [selected])

	const questions: IntakeSurveyQuestion[] = [
		{
			id: "referral_channel",
			question: t("intakeSurveyDialog.howDidYouHear"),
			choices: [
				t("intakeSurveyDialog.choiceYoutube"),
				t("intakeSurveyDialog.choiceInstagram"),
				t("intakeSurveyDialog.choiceTikTok"),
				t("intakeSurveyDialog.choiceFacebook"),
				t("intakeSurveyDialog.choiceNewsArticle"),
				t("intakeSurveyDialog.choiceGoogleSearch"),
				t("intakeSurveyDialog.choiceConference"),
				t("intakeSurveyDialog.choiceEmail"),
				t("intakeSurveyDialog.choiceFriend"),
				t("intakeSurveyDialog.choiceOther"),
			],
		},
		{
			id: "specialty",
			question: t("intakeSurveyDialog.whatKindOfPhotos"),
			choices: [
				t("intakeSurveyDialog.choiceWedding"),
				t("intakeSurveyDialog.choicePortrait"),
				t("intakeSurveyDialog.choiceFamily"),
				t("intakeSurveyDialog.choiceEvent"),
				t("intakeSurveyDialog.choiceSport"),
				t("intakeSurveyDialog.choiceLandscape"),
				t("intakeSurveyDialog.choiceRealEstate"),
				t("intakeSurveyDialog.choiceCommercial"),
				t("intakeSurveyDialog.choiceDocumentary"),
				t("intakeSurveyDialog.choiceOther"),
			],
		},
	]

	const currentQuestion = questions[currentQuestionIndex]

	const next = () => {
		if (currentQuestionIndex == questions.length - 1) {
			resetDialogs()
			props.resultHandler(result)
		} else {
			setCurrentQuestionIndex(currentQuestionIndex + 1)
			setSelected(null)
		}
	}

	const skip = () => {
		if (currentQuestion) {
			delete result[currentQuestion.id]
			setResult({ ...result })
		}
		next()
	}

	return (
		<Dialog trackingViewName="intake_survey_dialog" disableClose={true}>
			<div className="flex w-[512px] flex-col space-y-4">
				<div className="text-start text-lg">{currentQuestion?.question}</div>
				<div className="flex w-full flex-col items-start space-y-2">
					{currentQuestion?.choices.map((choice) => (
						<div
							key={choice}
							className="flex w-full shrink-0 flex-row items-center space-x-2 rounded-md"
							onClick={() => setSelected(choice)}
						>
							<input
								type="radio"
								checked={selected === choice}
								onChange={() => setSelected(choice)}
							/>
							<div className="text-sm">{choice}</div>
						</div>
					))}
				</div>
				<div className="flex w-full flex-row justify-end space-x-4">
					<Button trackingLabel="skip" onClick={skip}>
						{t("common:action.skip")}
					</Button>

					<Button
						trackingValue={selected ?? undefined}
						trackingLabel="confirm"
						variant="primary"
						disabled={!selected}
						onClick={next}
					>
						{currentQuestionIndex == questions.length - 1
							? t("intakeSurveyDialog.done")
							: t("intakeSurveyDialog.next")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
